<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { BaseIconName, BaseButtonType } from '@cplug/design-system/components/props'
import { BaseEmptyView } from '@cplug/design-system/components'
import { HttpStatusCode } from 'axios'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { type NuxtError } from '#app'
import { useThemePreferenceStore } from '~/domain/dashboard/settings/erp/shared'

const props = defineProps<{
  error: NuxtError
}>()

const { t } = useI18n()
const router = useRouter()

const authStore = useAuthenticationStore()
const themePreferenceStore = useThemePreferenceStore()
const { isDark } = storeToRefs(themePreferenceStore)

if (!authStore.logged) {
  router.replace('login')
}

const isNotFound = computed(() => props.error.statusCode === HttpStatusCode.NotFound)

function onClickReturnToHome (): void {
  router.back()
}

watch(isDark, (newTheme: boolean) => {
  document.documentElement.classList.toggle('dark', newTheme)
}, { immediate: true })
</script>

<template>
  <NuxtLayout>
    <div class="flex flex-col items-center justify-center h-screen">
      <BaseEmptyView
        :title="t(isNotFound ? 'NotFound' : 'InternalServerError')"
        :description="t(isNotFound ? 'NotFoundDescription' : 'InternalServerErrorDescription')"
        :icon-name="BaseIconName.Warning"
        :full-width="true"
        :secondary-button-text="isNotFound ? t('Back') : undefined"
        :secondary-button-type="BaseButtonType.Secondary"
        @click:secondary="onClickReturnToHome"
      />
    </div>

    <div id="modals" />
    <div id="dropdowns" />
    <div id="popovers" />
  </NuxtLayout>
</template>

<i18n lang="yaml">
pt-br:
  NotFound: 'Página não encontrada'
  NotFoundDescription: 'Não foi possível encontrar o que você procura. Verifique as informações enviadas e tente novamente.'
  InternalServerError: 'Algo deu errado'
  InternalServerErrorDescription: 'Não foi possível processar sua solicitação no momento. Por favor, tente novamente em alguns instantes.'
</i18n>
